import { Col, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { type BannerDataFragment } from '../../../api/fragments/BannerData';
import { ModuleType } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import ModuleSelectField from '../../../components/fields/ModuleSelectField';
import SingleUploadField from '../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../utilities/extensions';
import useSystemOptions from '../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';

const colSpan = { lg: 8, md: 12, xs: 24 };
type BannerFormProps = {
    updated?: string;
    banner?: BannerDataFragment;
    offset?: string;
    disabled?: boolean;
};
const BannerForm = ({ updated, banner, offset, disabled = false }: BannerFormProps) => {
    const { t } = useTranslation('bannerDetails');

    const company = useCompany(true);
    const { configurator } = useSystemOptions();
    const { yesNoSwitch } = useSystemSwitchData();

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('bannerDetails:sectionTitle')}>
                <Panel key={t('bannerDetails:sectionTitle')} header={t('bannerDetails:sectionTitle')}>
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('bannerDetails:fields.bannerImage', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                                name="bannerImage"
                                sizeLimitInMiB={20}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('bannerDetails:fields.mobileBannerImage', { returnObjects: true })}
                                disabled={disabled}
                                extensions={[...allowedExtensions.image, ...allowedExtensions.video]}
                                name="mobileBannerImage"
                                sizeLimitInMiB={20}
                                required
                            />
                        </Col>
                        {!updated ? (
                            <Col {...colSpan}>
                                <ModuleSelectField
                                    {...t('bannerDetails:fields.module', { returnObjects: true })}
                                    companyId={company?.id}
                                    disabled={disabled}
                                    moduleTypes={[ModuleType.ConfiguratorModule]}
                                    name="moduleId"
                                    required
                                />
                            </Col>
                        ) : (
                            <Col {...colSpan}>
                                <FormFields.DisplayField
                                    {...t('bannerDetails:fields.module', { returnObjects: true })}
                                    disabled={disabled}
                                    value={banner.module.displayName}
                                />
                            </Col>
                        )}
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('bannerDetails:fields.bannerText', { returnObjects: true })}
                                disabled={disabled}
                                name="bannerText"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('bannerDetails:fields.bannerTextPosition', { returnObjects: true })}
                                disabled={disabled}
                                name="bannerTextPosition"
                                options={configurator.bannerTextPositionOptions}
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('bannerDetails:fields.isActive', { returnObjects: true })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default BannerForm;
